import styles from 'css/src/components/common/video-preview/VideoPreview.module.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import type { VideoRender } from 'js/src/client';
import Tooltip from 'js/src/components/common/ui/Tooltip';
import FullscreenToggle from 'js/src/components/common/video-preview/FullscreenToggle';
import type { LuminaryVideoWithScrubberHandle } from 'js/src/components/common/video-preview/LuminaryVideoWithScrubber';
import LuminaryVideoWithScrubber from 'js/src/components/common/video-preview/LuminaryVideoWithScrubber';
import MuteToggle from 'js/src/components/common/video-preview/MuteToggle';
import type { VideoWithScrubberHandle } from 'js/src/components/common/video-preview/VideoWithScrubber';
import VideoWithScrubber from 'js/src/components/common/video-preview/VideoWithScrubber';

type PreviewElementHandle = LuminaryVideoWithScrubberHandle | VideoWithScrubberHandle;
import type { NumOpenCommentsPerScene } from 'js/src/data/comments';
import type { DeepReadonly } from 'js/src/libs/consts';
import { useLuminarySelector } from 'js/src/luminary/luminary-hooks';

const aspectRatioStyles: Record<string, string> = {
    landscape: styles['landscape'],
    square: styles['square'],
    vertical: styles['vertical'],
    fourfive: styles['fourfive'],
};

export enum PLAYBACK_STATE {
    LOADING = 'loading',
    PLAYING = 'playing',
    PAUSED = 'paused',
    STOPPED = 'stopped',
}
export type SortedVideoSourceList = readonly DeepReadonly<VideoRender>[];
type VideoPreviewOwnProps = {
    autoplay?: boolean;
    enableKeyboardShortcuts?: boolean;
    landscapeContainer?: boolean;
    startMuted?: boolean;
    isHtmlVideo?: boolean;
    shouldVideoPause?: boolean;
    onBeforePlay?: () => void;
    onPlay?: () => void;
    onSeek?: (newTime: number) => void;
    numOpenCommentsPerScene?: NumOpenCommentsPerScene;
    commentMarkerCallback?: (time: number, forceCommentingMode: boolean) => void;
    enableComments?: boolean;
    showMuteIndicator?: boolean;
    showFullscreenButton?: boolean;
    sortedVideoSourceList?: SortedVideoSourceList;
    onCurrentTimeUpdate?: (currentTime: number) => void;
    /**
     * Time (ms) to seek to on mount. `undefined` or `0` means "no preference" — see
     * `LuminaryVideoWithScrubber.initialSeekTime` for sentinel semantics.
     */
    initialSeekTime?: number;
    onMutedChange?: (muted: boolean) => void;
};

type Props = VideoPreviewOwnProps;

const SEEK_DELTA_MS = 10000;
const FRAME_MS = 1000 / 30;

/**
    The VideoPreview component uses the luminary hooks to interact with whatever instance
    of luminary is already located in the component hierarchy. If you don't want to use
    an existing instance of luminary on the page, just wrap it in it's own LuminaryContextProvider:

    <LuminaryContextProvider minDimensionsResolution={###}>
        <VideoPreview />
    </LuminaryContextProvider>
*/
const VideoPreview = React.memo(function VideoPreview({
    autoplay,
    enableKeyboardShortcuts = false,
    landscapeContainer,
    startMuted,
    isHtmlVideo,
    shouldVideoPause,
    onBeforePlay,
    onPlay,
    onSeek,
    numOpenCommentsPerScene,
    commentMarkerCallback,
    enableComments,
    showMuteIndicator,
    showFullscreenButton = true,
    sortedVideoSourceList,
    onCurrentTimeUpdate,
    initialSeekTime,
    onMutedChange,
}: Props) {
    const aspectRatio = useLuminarySelector((luminary) => luminary.aspectRatio, []);

    const [playback, setPlayback] = useState(PLAYBACK_STATE.LOADING);
    const [muted, setMuted] = useState(startMuted === true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const previewElementRef = useRef<PreviewElementHandle>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const callbackRef = useRef<(() => void) | null>(null);

    const setPlaybackState = useCallback(
        (newPlaybackState: PLAYBACK_STATE, callback?: () => void) => {
            callbackRef.current = callback ?? null;
            setPlayback(newPlaybackState);
        },
        [],
    );

    useEffect(() => {
        if (callbackRef.current) {
            callbackRef.current();
            callbackRef.current = null;
        }
    }, [playback]);

    const setMutedState = useCallback((muteLevel: boolean) => {
        setMuted(muteLevel);
    }, []);

    const toggleMute = useCallback(() => {
        setMuted((m) => !m);
    }, []);

    useEffect(() => {
        onMutedChange?.(muted);
    }, [muted, onMutedChange]);

    const toggleFullscreen = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            el.requestFullscreen().catch(() => {});
        }
    }, []);

    useEffect(() => {
        const onFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement === containerRef.current);
        };
        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, []);

    const play = useCallback(
        (opts = { firstPlay: false }) => {
            typeof onBeforePlay === 'function' && onBeforePlay();
            if (!previewElementRef.current) throw new Error('No preview element ref found');
            previewElementRef.current.play(opts);
            typeof onPlay === 'function' && onPlay();
        },
        [onBeforePlay, onPlay],
    );

    const pause = useCallback(() => {
        if (!previewElementRef.current) throw new Error('No preview element ref found');
        previewElementRef.current.pause();
    }, []);

    const togglePlay = useCallback(() => {
        if (playback === PLAYBACK_STATE.PLAYING) {
            pause();
        } else {
            play();
        }
    }, [playback, pause, play]);

    useEffect(() => {
        if (!enableKeyboardShortcuts) return;
        const onKeyDown = (e: KeyboardEvent) => {
            const target = e.target;
            if (
                target instanceof HTMLInputElement ||
                target instanceof HTMLTextAreaElement ||
                target instanceof HTMLSelectElement ||
                target instanceof HTMLButtonElement ||
                (target instanceof HTMLElement && target.isContentEditable) ||
                (target instanceof HTMLElement && target.closest('[contenteditable="true"]'))
            ) {
                return;
            }

            if (e.metaKey || e.ctrlKey || e.altKey) {
                return;
            }

            const el = previewElementRef.current;
            if (
                !el ||
                typeof el.getCurrentTime !== 'function' ||
                typeof el.getDuration !== 'function' ||
                typeof el.seek !== 'function'
            ) {
                return;
            }
            const currentTime = el.getCurrentTime();
            const duration = el.getDuration();
            const shift = e.shiftKey;
            const key = e.key.toLowerCase();
            switch (key) {
                case 'j':
                case 'arrowleft':
                    if (shift) {
                        el.seek(Math.max(0, currentTime - FRAME_MS));
                    } else {
                        el.seek(Math.max(0, currentTime - SEEK_DELTA_MS));
                    }
                    e.preventDefault();
                    break;
                case 'k':
                case ' ':
                    togglePlay();
                    e.preventDefault();
                    break;
                case 'm':
                    toggleMute();
                    e.preventDefault();
                    break;
                case 'l':
                case 'arrowright':
                    if (shift) {
                        el.seek(Math.min(duration, currentTime + FRAME_MS));
                    } else {
                        el.seek(Math.min(duration, currentTime + SEEK_DELTA_MS));
                    }
                    e.preventDefault();
                    break;
                default:
                    break;
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [enableKeyboardShortcuts, togglePlay, toggleMute]);

    useEffect(() => {
        if (autoplay !== false) {
            const hasInitialSeek = initialSeekTime !== undefined && initialSeekTime > 0;
            play({ firstPlay: !hasInitialSeek });
        }
        // initialSeekTime is intentionally only read on mount; subsequent changes shouldn't retrigger autoplay.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoplay, play]);

    useEffect(() => {
        if (shouldVideoPause) {
            pause();
        }
    }, [shouldVideoPause, pause]);

    const scrubberTooltipTitle = enableKeyboardShortcuts ? (
        <div>
            <div>
                Jump forward <kbd className="l5-tooltip-kbd">→</kbd>
            </div>
            <div>
                Jump back <kbd className="l5-tooltip-kbd">←</kbd>
            </div>
        </div>
    ) : undefined;

    const videoProps = {
        autoplay: autoplay,
        landscapeContainer: landscapeContainer,
        onSeek: onSeek,
        numOpenCommentsPerScene: numOpenCommentsPerScene,
        commentMarkerCallback: commentMarkerCallback,
        pauseVideo: pause,
        enableComments: enableComments,
        playbackState: playback,
        setPlaybackState: setPlaybackState,
        muted: muted,
        onCurrentTimeUpdate: onCurrentTimeUpdate,
        scrubberTooltipTitle,
    };
    const playPauseTooltipTitle =
        enableKeyboardShortcuts && (playback === PLAYBACK_STATE.PLAYING ? 'Pause' : 'Play');

    const playPauseIcon = (
        <div
            className={classNames('text-center p-l5-8', styles['video-preview-play-pause'], {
                [styles['show-on-hover']]: playback === PLAYBACK_STATE.PLAYING,
            })}
        >
            <span translate="no" className="material-symbols-outlined material-symbols-f1">
                {playback === PLAYBACK_STATE.STOPPED
                    ? 'replay'
                    : playback === PLAYBACK_STATE.PLAYING
                      ? 'pause'
                      : 'play_arrow'}
            </span>
        </div>
    );

    const playPauseReplayOverlay = (
        <div
            className={`${styles['video-preview-overlay']} d-flex justify-content-center align-items-center position-absolute top-0 start-0 end-0 w-100`}
        >
            {playPauseTooltipTitle ? (
                <Tooltip title={playPauseTooltipTitle} shortcut="Space">
                    {playPauseIcon}
                </Tooltip>
            ) : (
                playPauseIcon
            )}
        </div>
    );

    let videoElement: React.ReactNode;
    if (isHtmlVideo) {
        if (sortedVideoSourceList === undefined) {
            throw new Error('sortedVideoSourceList is required for HTML video');
        }
        videoElement = (
            <VideoWithScrubber
                ref={previewElementRef as React.RefObject<VideoWithScrubberHandle>}
                sortedVideoSourceList={sortedVideoSourceList}
                {...videoProps}
            />
        );
    } else {
        videoElement = (
            <LuminaryVideoWithScrubber
                ref={previewElementRef as React.RefObject<LuminaryVideoWithScrubberHandle>}
                {...videoProps}
                initialSeekTime={initialSeekTime}
            />
        );
    }

    return (
        <div
            ref={containerRef}
            className={classNames(
                styles['video-preview'],
                landscapeContainer ? styles['landscape'] : aspectRatioStyles[aspectRatio],
            )}
            data-testid="video-preview"
        >
            <div
                className={classNames(
                    styles['preview-content'],
                    'top-corner-radius cursor-pointer',
                    {
                        [styles['stopped']]: playback === PLAYBACK_STATE.STOPPED,
                    },
                )}
                onClick={togglePlay}
            >
                {playPauseReplayOverlay}
                {/* mute button */}
                {showMuteIndicator && (
                    <div className={styles['mute-toggle-wrapper']}>
                        <MuteToggle
                            muted={muted}
                            toggleMute={setMutedState}
                            tooltipTitle={enableKeyboardShortcuts ? 'Mute' : undefined}
                            tooltipShortcut={enableKeyboardShortcuts ? 'M' : undefined}
                        />
                    </div>
                )}
                {showFullscreenButton && document.fullscreenEnabled && (
                    <div className={styles['fullscreen-toggle-wrapper']}>
                        <FullscreenToggle
                            isFullscreen={isFullscreen}
                            toggleFullscreen={toggleFullscreen}
                            tooltipTitle={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                        />
                    </div>
                )}
                {/* main slide markers + luminary canvas/video element */}
                {videoElement}
            </div>
        </div>
    );
});

export default VideoPreview;
